import { compactHttpErrorForUi } from './httpErrorFormat';
import { fetchWithDevProxyFallback } from './fetchWithDevProxyFallback';

export type GeneralInfoPicklistItem = {
  id: number;
  label: string;
};

/** UI state supplied to `DynamicRecordForm` for register country/state pickers (and optional asset-type catalog). */
export type GeneralInfoPicklistsProp = {
  countries: GeneralInfoPicklistItem[];
  countriesStatus: 'idle' | 'loading' | 'ready' | 'error';
  countriesError: string | null;
  regions: GeneralInfoPicklistItem[];
  regionsStatus: 'idle' | 'loading' | 'ready' | 'error';
  regionsError: string | null;
  /** When set (Create Asset), root `asset_type_id` renders as a dropdown from `GET /assets/asset-types`. */
  assetTypes?: GeneralInfoPicklistItem[];
  assetTypesStatus?: 'idle' | 'loading' | 'ready' | 'error';
  assetTypesError?: string | null;
};

function extractPicklistRows(raw: unknown): unknown[] | null {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const k of [
      'data',
      'countries',
      'regions',
      'region',
      'districts',
      'district',
      'asset_types',
      'items',
      'result',
      'states',
      'state',
    ]) {
      const v = o[k];
      if (Array.isArray(v)) {
        return v;
      }
    }
  }
  return null;
}

function coercePicklistRow(row: unknown): GeneralInfoPicklistItem | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const r = row as Record<string, unknown>;
  /** Prefer subdivision ids before `country_id`: region rows often repeat the same `country_id`. */
  const idRaw =
    r.id ??
    r.district_id ??
    r.districtId ??
    r.region_id ??
    r.regionId ??
    r.state_id ??
    r.stateId ??
    r.country_id ??
    r.asset_type_id;
  const id = typeof idRaw === 'number' && Number.isFinite(idRaw) ? idRaw : Number(idRaw);
  if (!Number.isFinite(id)) {
    return null;
  }
  const labelRaw =
    r.name ??
    r.label ??
    r.title ??
    r.region_name ??
    r.state_name ??
    r.district_name ??
    r.districtName ??
    r.country_name ??
    r.asset_type_name ??
    r.type_name ??
    r.description;
  const label =
    typeof labelRaw === 'string' && labelRaw.trim().length > 0 ? labelRaw.trim() : `#${id}`;
  return { id, label };
}

export function parseGeneralInfoPicklist(json: unknown): GeneralInfoPicklistItem[] {
  const rows = extractPicklistRows(json);
  if (!rows) {
    return [];
  }
  const out: GeneralInfoPicklistItem[] = [];
  for (const row of rows) {
    const item = coercePicklistRow(row);
    if (item) {
      out.push(item);
    }
  }
  return out;
}

async function loadGeneralInfoJson(
  pathTail: string,
  headers: Headers,
): Promise<unknown> {
  const { response } = await fetchWithDevProxyFallback(pathTail, {
    method: 'GET',
    headers,
    cache: 'no-store',
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(compactHttpErrorForUi(response.status, response.statusText, text));
  }
  if (!text.trim()) {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error('Response is not valid JSON.');
  }
}

export async function loadGeneralInfoCountries(headers: Headers): Promise<GeneralInfoPicklistItem[]> {
  const json = await loadGeneralInfoJson('general-info/countries', headers);
  return parseGeneralInfoPicklist(json);
}

export async function loadGeneralInfoRegions(
  countryId: number,
  headers: Headers,
): Promise<GeneralInfoPicklistItem[]> {
  const json = await loadGeneralInfoJson(`general-info/regions/${countryId}`, headers);
  return parseGeneralInfoPicklist(json);
}
